import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { AuthOptions, TwoFactorSetup } from '../api/types'
import { useAuth, type LoginStep } from '../auth/AuthProvider'
import { config, isEntraConfigured } from '../config'
import { Footer } from '../components/Footer'
import { QrCode } from '../components/QrCode'
import { RecoveryCodeList } from '../components/RecoveryCodeList'
import { PasswordResetForm } from '../components/PasswordResetForm'

/**
 * Login-siden. To muligheder side om side (docs/DECISIONS.md beslutning 013):
 * - "Log ind med Microsoft" for medarbejdere med 365-konto
 * - e-mail + adgangskode for lokale brugere oprettet af en administrator
 * Hvilke der vises, styres af API-et (GET /api/auth/options).
 *
 * Lokalt login kan have et trin mere: en kode fra authenticator-appen, eller -
 * for en superadministrator der aldrig har sat det op - selve tilmeldingen
 * (beslutning 035). Tilmeldingen ligger her frem for inde i systemet, fordi
 * kravet ellers kunne springes over ved bare at lade være.
 */
export function LoginPage() {
  const { login, loginLocal, error, isLoading } = useAuth()
  const [options, setOptions] = useState<AuthOptions | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState<LoginStep>({ kind: 'done' })
  // "Glemt adgangskode?" (beslutning 078) - og beskeden bagefter.
  const [resetting, setResetting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<AuthOptions>('/api/auth/options')
      .then(setOptions)
      .catch(() => setOptions({ microsoftLoginEnabled: isEntraConfigured, localLoginEnabled: true }))
  }, [])

  const showMicrosoft = options?.microsoftLoginEnabled ?? isEntraConfigured
  const showLocal = options?.localLoginEnabled ?? true
  // Kun lokale konti har en adgangskode her - og kun når mailen kan sende koden.
  const canReset = showLocal && (options?.passwordResetEnabled ?? false)

  async function submitLocal(event: React.FormEvent) {
    event.preventDefault()
    setLocalError(null)
    setBusy(true)
    try {
      setStep(await loginLocal(email.trim(), password))
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Login mislykkedes.')
    } finally {
      // Adgangskoden skal ikke ligge i hukommelsen, mens koden tastes.
      setPassword('')
      setBusy(false)
    }
  }

  /** Tilbage til første trin, f.eks. hvis billetten er udløbet. */
  function startOver(message?: string) {
    setStep({ kind: 'done' })
    setLocalError(message ?? null)
  }

  return (
    <div className="login">
      <div className="login__card">
        <img className="login__logo" src="/logo/tc-logo.svg" alt="theodor christiansen" />
        <h1 style={{ fontSize: 22 }}>{config.appName}</h1>

        {step.kind === 'code' && <TwoFactorStep challenge={step.challenge} onCancel={startOver} />}

        {step.kind === 'enrol' && <EnrolmentStep challenge={step.challenge} onCancel={startOver} />}

        {step.kind === 'done' && resetting && (
          <PasswordResetForm
            initialEmail={email}
            onDone={(message) => {
              setResetting(false)
              setLocalError(null)
              setNotice(message)
            }}
            onCancel={() => setResetting(false)}
          />
        )}

        {step.kind === 'done' && !resetting && (
          <>
            <p className="muted small" style={{ marginTop: 8 }}>
              Log ind for at se projekter, opgaver og deadlines.
            </p>

            {showMicrosoft && (
              <button className="btn btn--microsoft" onClick={() => void login()} disabled={isLoading}>
                Log ind med Microsoft
              </button>
            )}

            {showMicrosoft && showLocal && (
              <div className="row" style={{ margin: '18px 0 6px', gap: 10 }}>
                <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--border)' }} />
                <span className="tiny muted">eller uden Microsoft-konto</span>
                <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--border)' }} />
              </div>
            )}

            {showLocal && (
              <form onSubmit={submitLocal} style={{ textAlign: 'left', marginTop: showMicrosoft ? 0 : 18 }}>
                <label className="field">
                  <span className="field__label">E-mail</span>
                  <input
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </label>
                <label className="field">
                  <span className="field__label">Adgangskode</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </label>
                <button className="btn btn--primary" style={{ width: '100%' }} type="submit" disabled={busy}>
                  {busy ? 'Logger ind ...' : 'Log ind'}
                </button>
                {/* Kun for konti med adgangskode her - Microsoft-konti skifter den hos Microsoft. */}
                {canReset && (
                  <button
                    type="button"
                    className="btn btn--link tiny"
                    style={{ marginTop: 10 }}
                    onClick={() => {
                      setLocalError(null)
                      setNotice(null)
                      setResetting(true)
                    }}
                  >
                    Glemt adgangskode?
                  </button>
                )}
              </form>
            )}

            {!showMicrosoft && !showLocal && (
              <div className="error-box" style={{ marginTop: 16, textAlign: 'left' }}>
                Ingen loginmetode er konfigureret. Sæt <code>VITE_ENTRA_*</code> (Microsoft) eller{' '}
                <code>JWT_SECRET</code> på API-et (lokale brugere).
              </div>
            )}
          </>
        )}

        {notice && step.kind === 'done' && !resetting && (
          <div className="map-notice" style={{ marginTop: 16, textAlign: 'left' }}>
            {notice}
          </div>
        )}

        {(localError || error) && (
          <div className="error-box" style={{ marginTop: 16, textAlign: 'left' }}>
            {localError ?? error}
          </div>
        )}

        <Footer className="login__footer" />
      </div>
    </div>
  )
}

/** Andet trin: de seks cifre fra appen - eller en genoprettelseskode. */
function TwoFactorStep({ challenge, onCancel }: { challenge: string; onCancel: (message?: string) => void }) {
  const { completeTwoFactor } = useAuth()
  const [code, setCode] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [useRecovery, setUseRecovery] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setMessage(null)
    setBusy(true)
    try {
      await completeTwoFactor(challenge, code.trim())
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Koden blev ikke godkendt.')
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} style={{ textAlign: 'left', marginTop: 18 }}>
      <p className="muted small" style={{ marginBottom: 14 }}>
        {useRecovery
          ? 'Tast en af dine genoprettelseskoder. Hver kode virker kun én gang.'
          : 'Åbn din authenticator-app og tast de seks cifre.'}
      </p>

      <label className="field">
        <span className="field__label">{useRecovery ? 'Genoprettelseskode' : 'Kode'}</span>
        <input
          // Engangskode: telefonen foreslår selv koden fra beskeder/appen.
          autoComplete="one-time-code"
          inputMode={useRecovery ? 'text' : 'numeric'}
          autoFocus
          required
          placeholder={useRecovery ? 'a3f9-2k7m' : '123 456'}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </label>

      {message && (
        <div className="error-box" style={{ marginBottom: 12 }}>
          {message}
        </div>
      )}

      <button className="btn btn--primary" style={{ width: '100%' }} type="submit" disabled={busy}>
        {busy ? 'Kontrollerer ...' : 'Fortsæt'}
      </button>

      <div className="row" style={{ justifyContent: 'space-between', marginTop: 12 }}>
        <button
          type="button"
          className="btn btn--link tiny"
          onClick={() => {
            setUseRecovery((value) => !value)
            setCode('')
            setMessage(null)
          }}
        >
          {useRecovery ? 'Brug koden fra appen' : 'Mistet telefonen?'}
        </button>
        <button type="button" className="btn btn--link tiny" onClick={() => onCancel()}>
          Annullér
        </button>
      </div>
    </form>
  )
}

/**
 * Tvungen tilmelding: brugeren skal have to-faktor, men har aldrig sat det op.
 * Der lukkes ikke ind, før en kode er bekræftet - ellers ville kravet være pynt.
 */
function EnrolmentStep({ challenge, onCancel }: { challenge: string; onCancel: (message?: string) => void }) {
  const { beginLoginEnrolment, completeLoginEnrolment } = useAuth()
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null)
  const [code, setCode] = useState('')
  const [done, setDone] = useState<{ codes: string[]; finish: () => void } | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false

    beginLoginEnrolment(challenge)
      .then((result) => {
        if (!cancelled) setSetup(result)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          onCancel(err instanceof Error ? err.message : 'Kunne ikke starte tilmeldingen.')
        }
      })

    return () => {
      cancelled = true
    }
  }, [challenge, beginLoginEnrolment, onCancel])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setMessage(null)
    setBusy(true)
    try {
      // Koderne skal ses, før brugeren lukkes videre - de kan ikke hentes igen.
      setDone(await completeLoginEnrolment(challenge, code.trim()))
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Koden blev ikke godkendt.')
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return <RecoveryCodeList codes={done.codes} onDone={done.finish} doneLabel="Jeg har gemt koderne - log mig ind" />
  }

  return (
    <form onSubmit={submit} style={{ textAlign: 'left', marginTop: 18 }}>
      <div className="error-box" style={{ marginBottom: 14, background: '#FFF7ED', borderColor: '#FED7AA', color: '#9A3412' }}>
        Din rolle kræver to-faktor. Sæt det op nu for at komme ind.
      </div>

      <ol className="small muted" style={{ paddingLeft: 18, margin: '0 0 14px' }}>
        <li>Åbn Microsoft Authenticator på telefonen.</li>
        <li>
          Vælg <strong>Tilføj konto</strong> → <strong>Anden konto</strong>.
        </li>
        <li>Scan koden herunder, og tast de seks cifre.</li>
      </ol>

      {setup ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <QrCode value={setup.uri} size={180} />
          </div>
          <p className="tiny muted" style={{ textAlign: 'center', marginBottom: 14 }}>
            Kan du ikke scanne? Tast nøglen i hånden:
            <br />
            <code style={{ wordBreak: 'break-all' }}>{setup.secret}</code>
          </p>
        </>
      ) : (
        <p className="small muted">Henter opsætning ...</p>
      )}

      <label className="field">
        <span className="field__label">Kode fra appen</span>
        <input
          autoComplete="one-time-code"
          inputMode="numeric"
          required
          placeholder="123 456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </label>

      {message && (
        <div className="error-box" style={{ marginBottom: 12 }}>
          {message}
        </div>
      )}

      <button className="btn btn--primary" style={{ width: '100%' }} type="submit" disabled={busy || !setup}>
        {busy ? 'Kontrollerer ...' : 'Slå to-faktor til og log ind'}
      </button>

      <button type="button" className="btn btn--link tiny" style={{ marginTop: 12 }} onClick={() => onCancel()}>
        Annullér
      </button>
    </form>
  )
}
